import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import {
  ReactiveFormsModule,
  FormGroup,
  FormControl,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MessageHandlerService } from '@core/services/message-handler/message-handler.service';
import { InputModule } from '@sharedModule/components/input/input.module';
import { ATTRIBUTES_CONSTANT } from '@sharedModule/constants/fields.constant';
import { MaterialModule } from '@sharedModule/material/material.module';
import { CountryInfo } from '@sharedModule/models/country.model';
import { ColumnPipe } from '@sharedModule/pipe/column/column.pipe';
import { removeEmptyStrings } from '@sharedModule/utils/form.utils';
import { Subject, takeUntil } from 'rxjs';
import { TransactionSettingsService } from '../../services/transaction-settings.service';
import { MasterTransactionSettingsInterface } from '../../types/transaction-settings';
import {
  UpsertPartnerTransactionSettingsPopupInterface,
  UpsertPartnerTransactionSettingsInterface,
} from '../../types/upsert-transaction-settings';

@Component({
  selector: 'app-upsert-partner-transaction-settings',
  standalone: true,
  imports: [
    ColumnPipe,
    InputModule,
    CommonModule,
    MaterialModule,
    ReactiveFormsModule,
  ],
  templateUrl: './upsert-partner-transaction-settings.component.html',
  styleUrl: './upsert-partner-transaction-settings.component.scss',
})
export class UpsertPartnerTransactionSettingsComponent
  implements OnInit, OnDestroy
{
  public readonly attrConstant = ATTRIBUTES_CONSTANT.settings.transaction;
  upsertTransactionSettingFrom!: FormGroup;
  txnSettings!: MasterTransactionSettingsInterface[];
  sourceCountryList!: CountryInfo[];
  destCountryList!: CountryInfo[];
  isSubmitLoading!: boolean;
  #destroy$ = new Subject<void>();

  constructor(
    private messageHandlerService: MessageHandlerService,
    @Inject(MAT_DIALOG_DATA)
    public txnData: UpsertPartnerTransactionSettingsPopupInterface,
    private transactionSettingsService: TransactionSettingsService,
    public closeDialogRef: MatDialogRef<UpsertPartnerTransactionSettingsComponent>
  ) {}

  ngOnInit(): void {
    const isUpdate = this.txnData.isUpdate;
    const txnSetting = this.txnData.selectedTransactionSetting;
    this.upsertTransactionSettingFrom = new FormGroup({
      [this.attrConstant.srcCountry.key]: new FormControl(
        isUpdate ? { value: txnSetting.source, disabled: true } : '',
        [Validators.required]
      ),
      [this.attrConstant.destCountry.key]: new FormControl(
        isUpdate ? { value: txnSetting.destination, disabled: true } : '',
        [Validators.required]
      ),
      name: new FormControl('', [Validators.required]),
    });
    this.getTransactionSettings();
  }

  onSourceCountryChange(event: { value: string }): void {
    this.upsertTransactionSettingFrom.patchValue({
      [this.attrConstant.destCountry.key]: '',
    });
    this.loadDestinationCountries(event.value);
  }

  private loadDestinationCountries(sourceCountry: string): void {
    this.transactionSettingsService
      .getDestinationCountryDropdown({
        src_country: sourceCountry,
      })
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) {
            if (x.response === null) {
              this.messageHandlerService.show(
                'No destination country added for selected source country'
              );
              this.destCountryList = [];
              return;
            }
            const countries = x.response.destination_currency;
            if (countries && countries.length > 0) {
              this.destCountryList = countries;
            }
          }
        },
      });
  }

  getTransactionSettings(): void {
    this.transactionSettingsService
      .getMasterPartnerTransactionSettings({})
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) {
            this.txnSettings = x.response[this.txnData.column];
            if (this.txnData.isUpdate)
              this.upsertTransactionSettingFrom.patchValue({
                name: this.txnData.selectedTransactionSetting.data.code,
              });
          }
        },
      });
  }

  onSubmit(): void {
    if (this.upsertTransactionSettingFrom.valid) {
      this.isSubmitLoading = true;
      const payload = this.getPayload();
      if (this.txnData.isUpdate) this.updateTransactionSetting(payload);
      else this.addTransactionSetting(payload);
    }
  }

  addTransactionSetting(
    formValue: UpsertPartnerTransactionSettingsInterface
  ): void {
    this.transactionSettingsService
      .addPartnerTransactionSetting(formValue)
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) {
            this.messageHandlerService.show(x.message);
            this.closeDialogRef.close(true);
          }
        },
        error: error => {
          console.log(error);
          this.closeDialogRef.close(false);
        },
      });
  }

  updateTransactionSetting(
    formValue: UpsertPartnerTransactionSettingsInterface
  ): void {
    this.transactionSettingsService
      .updatePartnerTransactionSetting({ ...formValue, id: this.txnData.id })
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) {
            this.messageHandlerService.show(x.message);
            this.closeDialogRef.close(true);
          }
        },
        error: error => {
          console.log(error);
          this.closeDialogRef.close(false);
        },
      });
  }

  getPayload(): UpsertPartnerTransactionSettingsInterface {
    const formValue = this.upsertTransactionSettingFrom.getRawValue();
    const selectedTxnSetting = this.txnSettings.find(
      i => i.code === formValue.name
    );
    const dynamicString =
      this.txnData.column === 'payment_method_codes' ? 'imgPath' : 'value';
    const payload = {
      src_country: formValue.src_country,
      dest_country: formValue.dest_country,
      column: this.txnData.column,
      code: selectedTxnSetting?.code,
      name: selectedTxnSetting?.name,
      description: selectedTxnSetting?.description ?? '',
      [dynamicString]: selectedTxnSetting?.value ?? '',
    };
    return removeEmptyStrings(payload);
  }

  ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }
}
